import React from 'react';
import {
  InfiniteLoader,
  List,
  AutoSizer,
  ListRowProps,
  CellMeasurer,
  CellMeasurerCache,
  CellMeasurerCacheParams,
  ListProps,
  IndexRange,
  WindowScroller,
} from 'react-virtualized';
import { InfiniteScrollContainer } from './InfiniteScroll.styles';
import 'react-virtualized/styles.css';

export interface ListRowPropsExtended extends ListRowProps {
  registerChild?: (element: Element) => void;
  measure?: () => void;
  cache: CellMeasurerCache;
}

export type AdjustedListProps = Pick<
  ListProps,
  Exclude<keyof ListProps, 'rowHeight' | 'height' | 'rowCount' | 'width'>
>;

interface Props {
  data: Array<any>;
  total: number;
  threshold?: number;
  loadMoreItems: ({ startIndex, stopIndex }: IndexRange) => Promise<any>;
  rowRenderer: (props: ListRowPropsExtended) => React.ReactNode;
  listProps?: AdjustedListProps;
  cellMeasurerCacheProps?: CellMeasurerCacheParams;
}

interface RowLoadedProps {
  index: number;
}

const InfiniteScroll = ({
  total,
  threshold = 15,
  loadMoreItems,
  data,
  rowRenderer,
  listProps,
  cellMeasurerCacheProps,
}: Props & AdjustedListProps & CellMeasurerCacheParams): React.ReactElement => {
  const isRowLoaded = ({ index }: RowLoadedProps): boolean => {
    return !!data[index];
  };

  const cache = new CellMeasurerCache({
    defaultHeight: 50,
    fixedWidth: true,
    ...cellMeasurerCacheProps,
  });

  const rowRendererWrapper = (props: ListRowProps): React.ReactNode => {
    return (
      <CellMeasurer cache={cache} key={props.key} parent={props.parent} rowIndex={props.index} columnIndex={0}>
        {({ registerChild, measure }) => {
          return rowRenderer({
            ...props,
            registerChild,
            measure,
            cache,
          });
        }}
      </CellMeasurer>
    );
  };

  return (
    <InfiniteScrollContainer>
      <InfiniteLoader isRowLoaded={isRowLoaded} rowCount={total} loadMoreRows={loadMoreItems} threshold={threshold}>
        {({ onRowsRendered, registerChild }) => (
          <WindowScroller>
            {({ height, onChildScroll, isScrolling, scrollTop }) => (
              <AutoSizer disableHeight onResize={() => cache.clearAll()}>
                {({ width }) => (
                  <List
                    {...listProps}
                    autoHeight
                    onRowsRendered={onRowsRendered}
                    rowCount={data.length}
                    columnWidth={cache.columnWidth}
                    deferredMeasurementCache={cache}
                    rowHeight={cache.rowHeight}
                    height={height || 100}
                    width={width || 100}
                    rowRenderer={rowRendererWrapper}
                    onScroll={onChildScroll}
                    isScrolling={isScrolling}
                    ref={registerChild}
                    scrollTop={scrollTop}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteLoader>
    </InfiniteScrollContainer>
  );
};

export default InfiniteScroll;
